import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Download,
  ExternalLink,
  LucideLoaderCircle,
  Users,
  Zap,
  X,
  RefreshCw,
  ListTodo,
  PencilIcon,
  ClipboardList,
  Coins,
  PieChart,
  Clock,
  Droplets,
  ChevronDown,
  Wallet,
  TrendingUp,
  Lock,
  Edit2,
  ListPlus,
  MoveRight,
  CheckCircle2, 
  XCircle,
  Circle,
  Clock8,
  Loader2,
  User
} from "lucide-react";
import { toast } from "react-toastify";
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import InnovatorsTable from "../../components/projects-component/InnovatorsTable";
import EmissionsHistoryTable from "../../components/projects-component/EmissionsHistoryTable";
import skyLoader from "../../assets/home-assets/skyLoader.gif";
import { useWallet } from "../../context/WalletContext";
import { useContractServices } from "../../context/ContractServicesContext";
import { projectAPI, taskAPI, innovatorAPI } from "../../services/api";
import { ethers } from "ethers";
import RedeemIUModal from "../../components/projects-component/RedeemIUModal";
import InvestIUModal from "../../components/projects-component/InvestIUModal";
import StakeProjectModal from "../../components/projects-component/StakeProjectModal";
// Import components for creator functionality
import EditProjectForm from "../../components/portfolio-component/EditProjectForm";
import { TaskFormModal } from "../../components/kanban-board/task-components/TaskFormModal";


function ProjectDetails() {
  const { projectTokenId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskApplicationForm, setShowTaskApplicationForm] = useState(false);
  const [showTaskSubmissionForm, setShowTaskSubmissionForm] = useState(false);
  const [showInvestForm, setShowInvestForm] = useState(false);
  const [showStakeForm, setShowStakeForm] = useState(false);
  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [userCelBalance, setUserCelBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [remainingInvestorAllocation, setRemainingInvestorAllocation] = useState(null);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const { walletAddress, connectWallet, innovatorProfile, chainId, isConnected } = useWallet();
  const { services, executeContractTransaction, isReady } = useContractServices();
  const [projectTreasury, setProjectTreasury] = useState({ formatted: '0', raw: null });
  const [projectStakingScore, setProjectStakingScore] = useState({ formatted: '0', raw: null });
  const [loadingContractData, setLoadingContractData] = useState(false);
  const [projectTotalStaked, setProjectTotalStaked] = useState({ formatted: '0', raw: null });
  const [remainingInvestorIUs, setRemainingInvestorIUs] = useState({ formatted: '0', raw: null });
  const [remainingContributorIUs, setRemainingContributorIUs] = useState({ formatted: '0', raw: null });
  const [loadingIUAllocation, setLoadingIUAllocation] = useState(false);
  const [projectSupply, setProjectSupply] = useState({ formatted: '0', raw: null });
  const [projectPrice, setProjectPrice] = useState({ formatted: '0', raw: null });
  const [projectData, setProjectData] = useState(null);
  const [contractCreators, setContractCreators] = useState([]);
  const [contractContributors, setContractContributors] = useState([]);
  const [contractInvestors, setContractInvestors] = useState([]);
  const [contractStakers, setContractStakers] = useState([]);
  const [totalStakersCount, setTotalStakersCount] = useState(0);
  const [loadingInnovators, setLoadingInnovators] = useState(false);
  const [emissionsData, setEmissionsData] = useState([]);
  const [loadingEmissions, setLoadingEmissions] = useState(false);
  
  // State variables for creator functionality
  const [isProjectCreator, setIsProjectCreator] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showEditTaskForm, setShowEditTaskForm] = useState(false);
  const [showApplicationsView, setShowApplicationsView] = useState(false);
  const [showSubmissionsView, setShowSubmissionsView] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [expandedTable, setExpandedTable] = useState(false);

  // State variable for IU circulating supply
  const [circulatingSupply, setCirculatingSupply] = useState({ formatted: '0', raw: null });
  const [innovators, setInnovators] = useState([]);

    /* ───────── fetch innovators once ───────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await innovatorAPI.getAll();
        setInnovators(data);
      } catch (error) {
        console.error("Error fetching innovators:", error);
        // Continue even if innovators can't be fetched
        setInnovators([]);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchProject = async () => {
      try {
        setLoading(true);
        const response = await projectAPI.getById(projectTokenId);
        if (!response.data) {
          toast.error("Something went wrong");
          setError("Project not found");
          return;
        }
        if (!cancelled) {
          setProject(response.data);
          
          // Once the project is loaded, if we have services available, 
          // fetch blockchain data immediately
          if (isReady && services?.innovationUnits) {
            fetchInnovatorsFromContract();
            fetchContractData();
          }
        }
      } catch (error) {
        if (!cancelled) {
          setError(error.response?.data?.message || "Error loading project");
          toast.error(error.response?.data?.message || "Error loading project");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (projectTokenId) {
      fetchProject();
    }

    return () => {
      cancelled = true;
    };
  }, [projectTokenId, isReady, services]);

  useEffect(() => {
    if (project && project._id) {
      fetchTasks(project._id);
    }
  }, [project]);

  const fetchTasks = async (projectId) => {
    try {
      const response = await taskAPI.getByProject(projectId);
      setTasks(response.data || []);
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const handleInvest = async (e) => {
    // Stop event propagation to prevent any bubbling issues
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    console.log("Invest button clicked");
    
    if (!walletAddress) {
      console.log("No wallet address, prompting to connect");
      toast.error("Please connect your wallet to invest in this project");
      connectWallet();
      return;
    }
    
    console.log("Opening invest form, current state:", { showInvestForm });
    setShowInvestForm(true);
    console.log("Form state after setting:", { showInvestForm: true });
    
    // When opening the invest form, fetch user's CEL balance
    fetchUserCelBalance();
    
    // Also fetch remaining investor allocation
    fetchRemainingInvestorIUs();
  };

  const handleStake = (e) => {
    // Stop event propagation to prevent any bubbling issues
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    console.log("Stake button clicked");
    
    if (!walletAddress) {
      console.log("No wallet address, prompting to connect");
      toast.error("Please connect your wallet to stake in this project");
      connectWallet();
      return;
    }
    
    console.log("Opening stake form, current state:", { showStakeForm });
    setShowStakeForm(true);
    console.log("Form state after setting:", { showStakeForm: true });
    
    // When opening the stake form, fetch user's CEL balance
    fetchUserCelBalance();
  };

  const handleRedeem = (e) => {
    // Stop event propagation to prevent any bubbling issues
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    console.log("Redeem button clicked");
    
    if (!walletAddress) {
      console.log("No wallet address, prompting to connect");
      toast.error("Please connect your wallet to redeem Innovation Units from this project");
      connectWallet();
      return;
    }
    
    console.log("Opening redeem form, current state:", { showRedeemForm });
    setShowRedeemForm(true);
    console.log("Form state after setting:", { showRedeemForm: true });
  };

  // General handler for closing modals
  const handleCloseModal = (modalType, refreshData = false) => {
    console.log(`Closing modal: ${modalType}, refresh data: ${refreshData}`);
    
    // Close the appropriate modal
    switch(modalType) {
      case 'invest':
        setShowInvestForm(false);
        break;
      case 'stake':
        setShowStakeForm(false);
        break;
      case 'redeem':
        setShowRedeemForm(false);
        break;
      case 'edit-project':
        setShowEditForm(false);
        break;
      case 'create-task':
        setShowTaskForm(false);
        break;
      default:
        console.warn(`Unknown modal type: ${modalType}`);
    }
    
    // Refresh data if needed
    if (refreshData && project) {
      toast.success("Refreshing project ...");
      fetchContractData();
      // If we have a project ID, also refresh the project from the API
      if (project._id) {
        projectAPI.getById(projectTokenId)
          .then(response => {
            if (response.data) {
              setProject(response.data);
            }
          })
          .catch(err => console.error("Error refreshing project:", err));
      }
    }
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
  };

  const handleCloseTaskDetails = () => {
    setSelectedTask(null);
  };

  const handleApplyForTask = (task) => {
    if (!walletAddress) {
      toast.error("Please connect your wallet to apply for tasks");
      connectWallet();
      return;
    }
    setSelectedTask(task);
    setShowTaskApplicationForm(true);
  };

  const handleSubmitWork = (task) => {
    if (!walletAddress) {
      toast.error("Please connect your wallet to submit work");
      connectWallet();
      return;
    }
    setSelectedTask(task);
    setShowTaskSubmissionForm(true);
  };

  const handleClaimTask = async (task) => {
    if (!walletAddress) {
      toast.error("Please connect your wallet to claim tasks");
      connectWallet();
      return;
    }
    try {
      await taskAPI.claimTask(task._id, { walletAddress });
      // Refresh tasks after claiming
      if (project && project._id) {
        fetchTasks(project._id);
      }
    } catch (error) {
      console.error("Error claiming task:", error);
      toast.error("Failed to claim task. Please try again.");
    }
  };

  const handleTaskApplication = async (applicationData) => {
    try {
      await taskAPI.applyForTask(selectedTask._id, applicationData);
      setShowTaskApplicationForm(false);
      toast.success("Application submitted successfully!");
      await fetchTasks(projectTokenId);
    } catch (error) {
      toast.error(`Error submitting application: ${error.message}`);
    }
  };

  const handleWorkSubmission = async (submissionData) => {
    try {
      await taskAPI.submitWork(selectedTask._id, submissionData);
      setShowTaskSubmissionForm(false);
      toast.success("Work submitted successfully!");
      await fetchTasks(projectTokenId);
    } catch (error) {
      toast.error(`Error submitting work: ${error.message}`);
    }
  };

  // Add useEffect for fetching staking parameters
  useEffect(() => {
    const fetchStakingParams = async () => {
      if (!isReady || !services?.projectStaking) {
        return;
      }
      
      try {
        const params = await services.projectStaking.getStakingParameters();
        console.log('Staking parameters:', params);
        // Set default lock duration to the minimum days or 30 days, whichever is greater
        const minDays = params.minLockDays || 7;
        const defaultDuration = Math.max(minDays, 30);
        
        // Ensure default duration is valid (between min and max)
        const maxDays = params.maxLockDays || 730;
        const validDefaultDuration = Math.min(Math.max(defaultDuration, minDays), maxDays);
        
        console.log(`Setting initial lock duration to ${validDefaultDuration} days (min: ${minDays}, max: ${maxDays})`);
        // No need to set lockDuration here, it's handled by the StakeProjectModal
      } catch (error) {
        console.error('Error fetching staking parameters:', error);
      }
    };
    
    fetchStakingParams();
  }, [isReady, services]);

  // Add this function to calculate unlock date
  const calculateUnlockDate = (durationDays) => {
    const unlockDate = new Date();
    unlockDate.setDate(unlockDate.getDate() + parseInt(durationDays));
    return unlockDate.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  // Add a function to fetch user's CEL token balance
  const fetchUserCelBalance = async () => {
    if (!isReady || !services?.celToken || !walletAddress) return;
    
    try {
      setLoadingBalance(true);
      const balance = await services.celToken.balanceOf(walletAddress);
      setUserCelBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Error fetching CEL balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Fetch user's CEL balance when wallet changes
  useEffect(() => {
    if (showStakeForm) {
      fetchUserCelBalance();
    }
  }, [walletAddress, isReady, services, showStakeForm]);

  // Modify the fetchRemainingInvestorAllocation function
  const fetchRemainingInvestorAllocation = async (totalCost) => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      // First, get the project ID
      let projectId;
      if (project?.transactionData?.projectId) {
        projectId = parseInt(project.transactionData.projectId);
      } else if (project?.projectId) {
        projectId = parseInt(project.projectId);
      } else {
        projectId = parseInt(projectTokenId);
      }
      
      if (isNaN(projectId) || projectId <= 0) return;
      
      const remainingAllocation = await services.innovationUnits.getRemainingInvestorAllocation(projectId);
      console.log(`Remaining investor allocation after transaction: ${remainingAllocation} IUs`);
      
      // Update the displayed available IUs
      // This is a temporary solution until the full project data is refreshed
      const projectData = await services.innovationUnits.getProjectData(projectId);
      if (projectData) {
        const totalSupply = projectData.totalSupply;
        const investorsReservePercentage = projectData.investorsReservePercentage;
        const investorsAllocation = totalSupply.mul(investorsReservePercentage).div(10000);
        
        // Update the local state for display purposes
        const updatedAvailableIUs = ethers.utils.formatUnits(remainingAllocation, 0);
        console.log(`Updated available IUs: ${updatedAvailableIUs}`);
        
        // Update the remainingInvestorAllocation state for UI display
        setRemainingInvestorAllocation(updatedAvailableIUs);
        
        // This would be better handled by refetching the full project data,
        // but we're using a temporary approach to update the UI
        setProject(prevProject => ({
          ...prevProject,
          totalInvestors: (prevProject.totalInvestors || 0) + 1,
          totalInvestment: totalCost 
            ? (parseFloat(prevProject.totalInvestment || 0) + parseFloat(ethers.utils.formatEther(totalCost))).toString()
            : prevProject.totalInvestment
        }));
      }
    } catch (error) {
      console.error("Error fetching remaining allocation:", error);
    }
  };

  // Add a new function to fetch the remaining allocation
  const fetchRemainingInvestorIUs = async () => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      setLoadingAllocation(true);
      
      // Get the project ID
      let projectId;
      if (project?.transactionData?.projectId) {
        projectId = parseInt(project.transactionData.projectId);
      } else if (project?.projectId) {
        projectId = parseInt(project.projectId);
      } else {
        projectId = parseInt(projectTokenId);
      }
      
      if (isNaN(projectId) || projectId <= 0) {
        console.error("Invalid project ID");
        return;
      }
      
      const remaining = await services.innovationUnits.getRemainingInvestorAllocation(projectId);
      setRemainingInvestorAllocation(ethers.utils.formatUnits(remaining));
    } catch (error) {
      console.error("Error fetching remaining investor allocation:", error);
    } finally {
      setLoadingAllocation(false);
    }
  };

  // Function to handle successful redemption (refresh data)
  const handleRedeemSuccess = async (txHash) => {
    try {
      // Refresh project data
      const refreshedProject = await projectAPI.getById(projectTokenId);
      if (refreshedProject.data) {
        setProject(refreshedProject.data);
        toast.info("Project data refreshed after redemption", { autoClose: 3000 });
      }
      
      // Refresh blockchain data
      fetchContractData();
    } catch (refreshError) {
      console.error("Error refreshing project data:", refreshError);
    }
  };

  // Handle successful investment - refresh project data
  const handleInvestSuccess = async (txHash, investmentData) => {
    try {
      // Add the investment to the database
      if (txHash) {
        console.log("Updating database with investment data:", investmentData);
        try {
          // Get wallet address
          const walletAddress = await services.celToken.contract.signer.getAddress();
          
          // Create investment data record
          const projectInvestmentData = {
            walletAddress,
            iuAllocation: +investmentData.investUnits,
            investedAmount: parseFloat(ethers.utils.formatEther(investmentData.totalCost)),
            transactionHash: txHash
          };
          
          console.log("Prepared investment data:", projectInvestmentData);
          
          await projectAPI.investInProject(
            project.projectTokenId,
            projectInvestmentData
          );
          console.log("Database updated successfully");
          
          // Update the remaining investor allocation
          await fetchRemainingInvestorAllocation(investmentData.totalCost);
        } catch (dbError) {
          console.error("Error updating database:", dbError);
          toast.error("Transaction was successful, but there was an error updating our records. Please contact support.");
        }
      }
      
      // Refresh the entire project data
      const refreshedProject = await projectAPI.getById(projectTokenId);
      if (refreshedProject.data) {
        setProject(refreshedProject.data);
        toast.info("Project data refreshed with your investment", { autoClose: 3000 });
      }
      
      // Refresh blockchain data
      fetchContractData();
    } catch (refreshError) {
      console.error("Error refreshing project data:", refreshError);
      // Non-critical error, don't show to user since the investment was successful
    }
  };
  
  // Handle successful staking - refresh project data
  const handleStakeSuccess = async (txHash, stakeData) => {
    try {
      // Add the stake to the database
      if (txHash && stakeData) {
        try {
          // await projectAPI.stakeInProject(
          //   project.projectTokenId,
          //   stakeData
          // );
          console.log("Stake recorded in database successfully");
        } catch (dbError) {
          console.error("Error updating database with stake:", dbError);
          toast.error("Staking was successful, but there was an error updating our records. Please contact support.");
        }
      }
      
      // Refresh the project data
      const refreshedProject = await projectAPI.getById(projectTokenId);
      if (refreshedProject.data) {
        setProject(refreshedProject.data);
        toast.info("Project data refreshed after staking", { autoClose: 3000 });
      }
      
      // Refresh blockchain data
      fetchContractData();
    } catch (refreshError) {
      console.error("Error refreshing project data after staking:", refreshError);
    }
  };

  // Helper function to generate etherscan links based on the current chain
  const getExplorerUrl = (txHash) => {
    // Default to Optimism Sepolia if chainId is not available
    const network = !chainId ? 'sepolia-optimism' :
      chainId === 10 ? 'optimistic' : 
      chainId === 11155420 ? 'sepolia-optimism' : 
      'sepolia-optimism';
    
    return `https://${network}.etherscan.io/tx/${txHash}`;
  };

  // Add a new useEffect to fetch contract data when project information is loaded
  useEffect(() => {
    if (project && isReady && services) {
      fetchContractData();
    }
  }, [project, isReady, services]);

  // Update the fetchContractData function to also fetch IU supply and price
  const fetchContractData = async () => {
    if (!project || !isReady || !services) {
      console.log("Required data or services not available for contract data fetch");
      console.log("Project:", project);
      console.log("Services ready:", isReady);
      console.log("Services available:", Object.keys(services || {}));
      // toast.info("Wallet not connected or services not ready. Please connect your wallet.");
      return;
    }

    try {
      setLoadingContractData(true);
      
      // Convert projectTokenId to a number if it's a string
      const projectId = typeof project.projectTokenId === 'string' 
        ? parseInt(project.projectTokenId)
        : project.projectTokenId;
        
      console.log(`Fetching contract data for project ID: ${projectId}`);
      
      // Fetch project treasury balance
      if (services.innovationUnits) {
        console.log("Innovation Units service is available. Fetching data...");
        // Fetch project data which includes total supply and price
        try {
          console.log("Calling getProjectData...");
          const fullProjectData = await services.innovationUnits.getProjectData(projectId);
          console.log("Full project data from contract:", fullProjectData);
          
          if (fullProjectData) {
            setProjectData(fullProjectData);
            
            // Set total supply
            const formattedSupply = ethers.utils.formatUnits(fullProjectData.totalSupply, 0);
            console.log("Setting project supply:", formattedSupply);
            setProjectSupply({
              raw: fullProjectData.totalSupply,
              formatted: formattedSupply
            });
            
            // Set price per IU
            const formattedPrice = ethers.utils.formatEther(fullProjectData.initialPrice);
            console.log("Setting project price:", formattedPrice);
            setProjectPrice({
              raw: fullProjectData.initialPrice,
              formatted: formattedPrice
            });
          } else {
            console.warn("getProjectData returned null or undefined");
          }
        } catch (projectDataError) {
          console.error("Error fetching project data:", projectDataError);
          console.error("Error details:", {
            message: projectDataError.message,
            code: projectDataError.code,
            stack: projectDataError.stack
          });
        }
        
        try {
          console.log("Calling getProjectTreasuryBalance...");
          const treasuryData = await services.innovationUnits.getProjectTreasuryBalance(projectId);
          console.log("Contract treasury data:", treasuryData);
          if (treasuryData) {
            setProjectTreasury(treasuryData);
          } else {
            console.warn("getProjectTreasuryBalance returned null or undefined");
          }
        } catch (treasuryError) {
          console.error("Error fetching project treasury:", treasuryError);
          console.error("Error details:", {
            message: treasuryError.message,
            code: treasuryError.code,
            stack: treasuryError.stack
          });
        }
        
        // Fetch remaining IU allocations
        try {
          console.log("Fetching remaining IU allocations...");
          setLoadingIUAllocation(true);
          
          // Fetch remaining investor allocation
          console.log("Calling getRemainingInvestorAllocation...");
          const investorAllocation = await services.innovationUnits.getRemainingInvestorAllocation(projectId);
          console.log("Remaining investor allocation:", investorAllocation.toString());
          const formattedInvestorAllocation = ethers.utils.formatUnits(investorAllocation, 0);
          console.log("Formatted investor allocation:", formattedInvestorAllocation);
          setRemainingInvestorIUs({
            raw: investorAllocation,
            formatted: formattedInvestorAllocation
          });
          
          // Fetch remaining contributor allocation
          console.log("Calling getRemainingContributorAllocation...");
          const contributorAllocation = await services.innovationUnits.getRemainingContributorAllocation(projectId);
          console.log("Remaining contributor allocation:", contributorAllocation.toString());
          const formattedContributorAllocation = ethers.utils.formatUnits(contributorAllocation, 0);
          console.log("Formatted contributor allocation:", formattedContributorAllocation);
          setRemainingContributorIUs({
            raw: contributorAllocation,
            formatted: formattedContributorAllocation
          });
        } catch (allocationError) {
          console.error("Error fetching IU allocations:", allocationError);
          console.error("Error details:", {
            message: allocationError.message,
            code: allocationError.code,
            stack: allocationError.stack
          });
        } finally {
          setLoadingIUAllocation(false);
        }
      } else {
        console.warn("Innovation Units service not available");
        console.log("Available services:", Object.keys(services || {}));
      }
      
      // Fetch project staking score and total staked amount
      if (services.projectStaking) {
        console.log("Project Staking service is available. Fetching data...");
        // First try to get the staking score
        try {
          console.log("Calling getProjectStakingScore...");
          const stakingScoreData = await services.projectStaking.getProjectStakingScore(projectId);
          console.log("Contract staking score data:", stakingScoreData);
          if (stakingScoreData) {
            setProjectStakingScore(stakingScoreData);
          } else {
            console.warn("getProjectStakingScore returned null or undefined");
          }
        } catch (scoreError) {
          console.error("Error fetching staking score:", scoreError);
          console.error("Error details:", {
            message: scoreError.message,
            code: scoreError.code,
            stack: scoreError.stack
          });
        }
        
        // Then try to get the total staked amount
        try {
          console.log("Calling getProjectTotalStakedAmount...");
          const stakedAmountData = await services.projectStaking.getProjectTotalStakedAmount(projectId);
          console.log("Contract staked amount data:", stakedAmountData);
          
          // Set the project total staked amount
          if (stakedAmountData) {
            setProjectTotalStaked(stakedAmountData);
          } else {
            console.warn("getProjectTotalStakedAmount returned null or undefined");
          }
          
          // If we couldn't get the staking score but got the staked amount, use that instead
          if (stakedAmountData && projectStakingScore.raw?.isZero()) {
            setProjectStakingScore(stakedAmountData);
          }
        } catch (amountError) {
          console.error("Error fetching staked amount:", amountError);
          console.error("Error details:", {
            message: amountError.message,
            code: amountError.code,
            stack: amountError.stack
          });
        }
      } else {
        console.warn("Project Staking service not available");
        console.log("Available services:", Object.keys(services || {}));
      }
      
      // Show success message
      // toast.success("Blockchain data refreshed successfully!");
      
      // Log state after updates
      setTimeout(() => {
        console.log("=== STATE AFTER REFRESH ===");
        console.log("Project data:", projectData);
        console.log("Project supply:", projectSupply);
        console.log("Project price:", projectPrice);
        console.log("Remaining investor IUs:", remainingInvestorIUs);
        console.log("Remaining contributor IUs:", remainingContributorIUs);
      }, 1000); // Wait a second to ensure state updates have happened

      // Fetch circulating supply
      try {
        console.log("Calling getCirculatingSupply...");
        const circulatingData = await services.innovationUnits.getCirculatingSupply(projectId);
        console.log("Circulating supply data:", circulatingData);
        
        // Special handling for when the result is valid but potentially zero
        if (circulatingData) {
          console.log("Raw circulating supply:", circulatingData.raw?.toString());
          console.log("Formatted circulating supply:", circulatingData.formatted);
          
          // Check if we have a BigNumber (valid even if zero)
          if (circulatingData.raw && ethers.BigNumber.isBigNumber(circulatingData.raw)) {
            setCirculatingSupply(circulatingData);
            console.log("Successfully set circulating supply state:", circulatingData);
          } else {
            console.warn("getCirculatingSupply returned an invalid BigNumber:", circulatingData.raw);
          }
        } else {
          console.warn("getCirculatingSupply returned null or undefined");
        }
      } catch (circulatingError) {
        console.error("Error fetching circulating supply:", circulatingError);
        console.error("Error details:", {
          message: circulatingError.message,
          code: circulatingError.code,
          stack: circulatingError.stack
        });
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      toast.error("Failed to fetch blockchain data. Please try again.");
    } finally {
      setLoadingContractData(false);
    }
  };

  // Add a function to fetch stakers data from the contract
  const fetchProjectStakers = async (projectId) => {
    if (!isReady || !services?.projectStaking) {
      console.log("ProjectStaking service not available");
      return [];
    }
    
    try {
      console.log(`Attempting to fetch stakers for project ${projectId}`);
      
      // Use the new getProjectStakers function to fetch all stakers directly from the contract
      const stakersData = await services.projectStaking.getProjectStakers(projectId);
      console.log(`Found ${stakersData.stakers.length} stakers for project ${projectId}`);
      
      // Update the total stakers count state
      setTotalStakersCount(stakersData.stakers.length);
      
      // Format the data for the InnovatorsTable component
      const stakersList = stakersData.stakers.map((address, index) => ({
        walletAddress: address,
        stakedAmount: parseFloat(stakersData.amounts[index]),
        role: 'Staker'
      }));
      
      console.log(`Processed ${stakersList.length} stakers for the UI`);
      return stakersList;
    } catch (error) {
      console.error(`Error fetching stakers for project ${projectId}:`, error);
      
      // Fallback to the current user's stakes if we can't get all stakers
      let stakersList = [];
      
      if (walletAddress) {
        try {
          // Try to get the user's active stakes for this project
          const userStakes = await services.projectStaking.getUserActiveStakes(
            projectId,
            walletAddress
          );
          
          if (userStakes && userStakes.amounts) {
            // For each stake, create a staker entry
            for (let i = 0; i < userStakes.amounts.length; i++) {
              if (userStakes.amounts[i] && !userStakes.amounts[i].isZero()) {
                stakersList.push({
                  walletAddress: walletAddress,
                  stakedAmount: parseFloat(ethers.utils.formatEther(userStakes.amounts[i])),
                  unlockTime: userStakes.unlockTimes[i].toNumber(),
                  stakeIndex: userStakes.indexes[i].toNumber(),
                  role: 'Staker'
                });
              }
            }
            
            console.log(`Found ${stakersList.length} active stakes for current user as fallback`);
            // In fallback mode, we only know about the current user as a staker
            if (stakersList.length > 0) {
              setTotalStakersCount(1);
            } else {
              setTotalStakersCount(0);
            }
          }
        } catch (userStakesError) {
          console.error("Error fetching user stakes:", userStakesError);
          setTotalStakersCount(0);
        }
      } else {
        setTotalStakersCount(0);
      }
      
      return stakersList;
    }
  };

  // Update the fetchInnovatorsFromContract function to also fetch stakers
  const fetchInnovatorsFromContract = async () => {
    if (!isReady || !services?.innovationUnits || !project?.projectTokenId) {
      console.log("Services not ready or project ID not available for innovators fetch");
      return;
    }
    
    try {
      setLoadingInnovators(true);
      
      // Convert projectTokenId to a number if it's a string
      const projectId = typeof project.projectTokenId === 'string' 
        ? parseInt(project.projectTokenId)
        : project.projectTokenId;
      
      console.log(`Fetching innovators data for project ID: ${projectId}`);
      
      // Fetch creators with actual holdings using our new method
      try {
        console.log("Fetching creators with actual IU holdings");
        const creatorsWithHoldings = await services.innovationUnits.getProjectCreatorsWithHoldings(projectId);
        console.log("Creators with actual holdings data:", creatorsWithHoldings);
        
        // Set the creators with accurate IU holdings
        if (creatorsWithHoldings && creatorsWithHoldings.length > 0) {
          setContractCreators(creatorsWithHoldings);
        }
      } catch (creatorsError) {
        console.error("Error fetching creators with holdings:", creatorsError);
        
        
      }
      
      // Fetch contributors
      try {
        const contributorsData = await services.innovationUnits.getContributorsInfo(projectId);
        console.log("Contributors data from contract:", contributorsData);
        
        if (contributorsData && contributorsData.contributors.length > 0) {
          // Transform data to match the expected format for InnovatorsTable
          const formattedContributors = contributorsData.contributors.map((address, index) => ({
            walletAddress: address,
            // Convert BigNumber to string and then to number
            iuAllocation: parseInt(ethers.utils.formatUnits(contributorsData.amounts[index], 0)),
            role: 'Contributor'
          }));
          
          setContractContributors(formattedContributors);
        }
      } catch (contributorsError) {
        console.error("Error fetching contributors:", contributorsError);
      }
      
      // Fetch investors
      try {
        const investorsData = await services.innovationUnits.getInvestorsInfo(projectId);
        console.log("Investors data from contract:", investorsData);
        
        if (investorsData && investorsData.investors.length > 0) {
          // Transform data to match the expected format for InnovatorsTable
          const formattedInvestors = investorsData.investors.map((address, index) => ({
            walletAddress: address,
            // Convert BigNumber to string and then to number
            iuAllocation: parseInt(ethers.utils.formatUnits(investorsData.amounts[index], 0)),
            role: 'Investor'
          }));
          
          setContractInvestors(formattedInvestors);
        }
      } catch (investorsError) {
        console.error("Error fetching investors:", investorsError);
      }
      
      // Fetch stakers
      try {
        const stakersData = await fetchProjectStakers(projectId);
        console.log("Stakers data from contract:", stakersData);
        
        if (stakersData && stakersData.length > 0) {
          setContractStakers(stakersData);
        }
      } catch (stakersError) {
        console.error("Error fetching stakers:", stakersError);
      }
      
      // toast.success("Innovators data loaded from blockchain");
    } catch (error) {
      console.error("Error fetching innovators data:", error);
      toast.error("Failed to fetch innovators data. Please try again.");
    } finally {
      setLoadingInnovators(false);
    }
  };

  // Add function to navigate to task board
  const navigateToTaskBoard = () => {
    navigate(`/tasks?id=${projectTokenId}`);
  };

  // Add a function to fetch historical emissions data
  const fetchHistoricalEmissions = async () => {
    if (!isReady || !services?.emissionController || !project?.projectTokenId) {
      console.log("Services not ready or project ID not available for emissions fetch");
      return;
    }
    
    try {
      setLoadingEmissions(true);
      
      // Convert projectTokenId to a number if it's a string
      const projectId = typeof project.projectTokenId === 'string' 
        ? parseInt(project.projectTokenId)
        : project.projectTokenId;
      
      console.log(`Fetching historical emissions data for project ID: ${projectId}`);
      
      const historicalData = await services.emissionController.getHistoricalProjectEmissions(projectId);
      setEmissionsData(historicalData);
      
      // toast.success("Historical emissions data loaded successfully");
    } catch (error) {
      console.error("Error fetching historical emissions:", error);
      toast.error("Failed to fetch emissions data. Please try again.");
    } finally {
      setLoadingEmissions(false);
    }
  };

  // Check if the current wallet is a project creator
  const checkIfProjectCreator = () => {
    if (!walletAddress || !project) {
      return false;
    }
    
    // Check if the wallet address is in the contractCreators array
    const isCreator = contractCreators.length > 0 && contractCreators.some(
      creator => creator.walletAddress.toLowerCase() === walletAddress.toLowerCase()
    );

    // Also check the project.creators array if contractCreators is empty or as a fallback
    const isCreatorInOriginalArray = project.creators && project.creators.length > 0 && project.creators.some(
      creator => creator.walletAddress && creator.walletAddress.toLowerCase() === walletAddress.toLowerCase()
    );

    const result = isCreator || isCreatorInOriginalArray;
    console.log("Creator check result:", result, {
      contractCreatorsCount: contractCreators.length,
      originalCreatorsCount: project.creators?.length || 0,
      walletAddress,
      isCreator,
      isCreatorInOriginalArray,
    });
    
    return result;
  };

  // Functions for creator actions
  const handleEditProject = (e) => {
    // Stop event propagation to prevent any bubbling issues
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    console.log("Edit Project button clicked");
    
    if (!project) {
      console.log("No project data available");
      return;
    }
    
    console.log("Opening edit project form");
    setShowEditForm(true);
    console.log("Edit form state after setting:", true);
  };

  

  const handleCreateTask = (e) => {
    // Stop event propagation to prevent any bubbling issues
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    console.log("Create Task button clicked");
    
    if (!project) {
      console.log("No project data available");
      return;
    }
    
    console.log("Opening create task form");
    setShowTaskForm(true);
    console.log("Task form state after setting:", true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowEditTaskForm(true);
  };

  const handleViewApplications = (task) => {
    setSelectedTask(task);
    setShowApplicationsView(true);
  };

  const handleViewSubmissions = (task) => {
    setSelectedTask(task);
    setShowSubmissionsView(true);
  };

  const handleChangeTaskStatus = async (taskId, newStatus) => {
    try {
      await taskAPI.update(taskId, { status: newStatus });
      await fetchTasks(project._id);
      toast.success(`Task status updated to ${newStatus}`);
    } catch (err) {
      console.error("Error updating task status:", err);
      toast.error("Failed to update task status: " + err.message);
    }
  };

  const handleResponseToApplication = async (applicationId, status, feedback) => {
    try {
      await taskAPI.respondToApplication(applicationId, {
        status,
        feedback,
      });
      // Refresh the selected task data
      if (selectedTask) {
        const updatedTaskResponse = await taskAPI.getById(selectedTask._id);
        setSelectedTask(updatedTaskResponse.data);
      }
      await fetchTasks(project._id);
      toast.success(`Application ${status === 'approved' ? 'approved' : 'rejected'}`);
    } catch (err) {
      console.error("Error responding to application:", err);
      toast.error("Failed to respond to application: " + err.message);
    }
  };

  const handleReviewSubmission = async (submissionId, status, feedback) => {
    try {
      await taskAPI.reviewSubmission(submissionId, { status, feedback });
      toast.success("Submission review saved successfully!");
      
      // Refresh tasks after reviewing submission
      if (project && project._id) {
        fetchTasks(project._id);
      }
      
      // Close submission view
      setShowSubmissionsView(false);
    } catch (err) {
      console.error("Error reviewing submission:", err);
      toast.error("An error occurred while saving your review. Please try again.");
    }
  };


  const handleFormClose = (refreshNeeded = false) => {
    setShowEditForm(false);
    setShowTaskForm(false);
    setShowEditTaskForm(false);
    setShowApplicationsView(false);
    setShowSubmissionsView(false);
    setSelectedTask(null);

    if (refreshNeeded) {
      // Refresh the project details
      if (project && project.projectTokenId) {
        const refreshProject = async () => {
          try {
            console.log("Refreshing project with token ID:", project.projectTokenId);
            const response = await projectAPI.getById(project.projectTokenId);
            if (response.data) {
              setProject(response.data);
              toast.success("Project data refreshed");
            }
          } catch (err) {
            console.error("Error refreshing project:", err);
          }
        };

        refreshProject();
        // Also refresh tasks
        if (project._id) {
          fetchTasks(project._id);
        }
      }
    }
  };
  
  // Update isProjectCreator whenever relevant data changes
  useEffect(() => {
    const creatorStatus = checkIfProjectCreator();
    setIsProjectCreator(creatorStatus);
    console.log("Is project creator:", creatorStatus);
  }, [walletAddress, project, contractCreators]);

  // Add useEffect to fetch emissions data when project is loaded and services are ready
  useEffect(() => {
    if (project && isReady && services?.emissionController) {
      fetchHistoricalEmissions();
    }
  }, [project, isReady, services]);

  // Add a new useEffect to fetch innovators data (especially creators) when the project is loaded
  useEffect(() => {
    if (project && isReady && services?.innovationUnits && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchInnovatorsFromContract();
    }
  }, [project, isReady, services]);

  // Add useEffect to monitor form state changes
  useEffect(() => {
    console.log("showInvestForm state changed:", showInvestForm);
  }, [showInvestForm]);
  
  useEffect(() => {
    console.log("showStakeForm state changed:", showStakeForm);
  }, [showStakeForm]);
  
  useEffect(() => {
    console.log("showRedeemForm state changed:", showRedeemForm);
  }, [showRedeemForm]);
  
  useEffect(() => {
    console.log("showEditForm state changed:", showEditForm);
  }, [showEditForm]);
  
  useEffect(() => {
    console.log("showTaskForm state changed:", showTaskForm);
  }, [showTaskForm]);

  // Update useEffect to refresh creator allocations when project data is refreshed
  useEffect(() => {
    if (project && isReady && services?.innovationUnits && (contractCreators.length === 0 || projectSupply.raw)) {
      // If we have no creators loaded, or we just got the project supply, refresh creator data
      fetchInnovatorsFromContract();
    }
  }, [project, isReady, services, projectSupply.raw]);

  // Add a function to refresh all project data
  const refreshAllProjectData = async () => {
    if (!isReady || !services || !project) {
      toast.info("Wallet not connected or services not ready. Please connect your wallet.");
      return;
    }
    
    try {
      toast.info("Refreshing all project data...");
      
      // Get project ID
      const projectId = typeof project.projectTokenId === 'string' 
        ? parseInt(project.projectTokenId)
        : project.projectTokenId;
      
      // First refresh blockchain data
      await fetchContractData();
      
      // Then refresh creator data with actual holdings
      try {
        const creatorsWithHoldings = await services.innovationUnits.getProjectCreatorsWithHoldings(projectId);
        if (creatorsWithHoldings && creatorsWithHoldings.length > 0) {
          setContractCreators(creatorsWithHoldings);
        }
      } catch (creatorsError) {
        console.error("Error refreshing creators with holdings:", creatorsError);
      }
      
      // Then refresh other innovators data
      await fetchInnovatorsFromContract();
      
      // Then refresh emissions data if available
      if (services.emissionController) {
        await fetchHistoricalEmissions();
      }
      
      toast.success("All project data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing all project data:", error);
      toast.error("Failed to refresh all project data. Please try again.");
    }
  };

  // Add this function to get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Clock8 className="h-4 w-4 text-violet-500" />;
      case 'submitted':
        return <Circle className="h-4 w-4 text-amber-500" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  // Add this function to count tasks by status
  const getTaskCountsByStatus = () => {
    const counts = {
      total: tasks.length,
      backlog: 0,
      open: 0, 
      in_progress: 0,
      submitted: 0,
      approved: 0,
      rejected: 0
    };
    
    tasks.forEach(task => {
      if (counts.hasOwnProperty(task.status)) {
        counts[task.status]++;
      }
    });
    
    return counts;
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );

  if (error)
    return <div className="min-h-screen p-8 text-red-500">Error: {error}</div>;
  if (!project)
    return <div className="min-h-screen p-8">Project not found</div>;

  // Calculate available Innovation Units for investors
  const availableIUs = project.ownershipDistribution
    ? project.totalInnovationUnits *
      (project.ownershipDistribution.investors / 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/projects"
          className="inline-flex items-center text-blue-600 hover:text-indigo-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="font-medium">Back to Projects</span>
        </Link>

        {/* Project Header */}
        <div className="bg-white border border-indigo-100 rounded-xl shadow-md overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-5 border-b border-indigo-100">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-indigo-800 mb-3">
                  {project.title}
                  {process.env.NODE_ENV === 'development' && (
                    <button 
                      onClick={() => {
                        console.log("=== DEBUG INFO ===");
                        console.log("Project:", project);
                        console.log("Services ready:", isReady);
                        console.log("Available services:", services);
                        console.log("Project data:", projectData);
                        console.log("Project supply:", projectSupply);
                        console.log("Project price:", projectPrice);
                        console.log("Remaining investor IUs:", remainingInvestorIUs);
                        console.log("Remaining contributor IUs:", remainingContributorIUs);
                        
                        // Try to refresh data
                        fetchContractData();
                      }}
                      className="ml-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                    >
                      Debug
                    </button>
                  )}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-gray-600">
                  <span className="bg-yellow-400 text-black px-3 py-1.5 rounded-full text-sm font-medium">
                    {project.primaryDomain || project.industry}
                  </span>
                  {project.subdomains &&
                    project.subdomains.map((subdomain, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-200 text-purple-900 px-3 py-1.5 rounded-full text-sm font-medium"
                      >
                        {subdomain}
                      </span>
                    ))}
                  <span className="text-sm bg-green-200 text-indigo-900 px-3 py-1.5 rounded-full font-medium flex items-center">
                    <Calendar className="w-4 h-4 mr-1.5" />
                    Created on {new Date(project.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end w-full md:w-auto">
                {/* Standard buttons shown to all users */}
                <button
                  onClick={handleInvest}
                  data-action="invest"
                  className={`px-4 py-2 rounded-full flex items-center ${
                    !walletAddress
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm"
                  }`}
                >
                  <DollarSign className="inline-block mr-2 w-5 h-5" />
                  Invest
                </button>
                <button
                  onClick={handleStake}
                  data-action="stake"
                  className={`px-4 py-2 rounded-full flex items-center ${
                    !walletAddress
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-purple-600 hover:bg-purple-700 text-white transition-colors shadow-sm"
                  }`}
                >
                  <Zap className="inline-block mr-2 w-5 h-5" />
                  Stake
                </button>
                <button
                  onClick={handleRedeem}
                  data-action="redeem"
                  className={`px-4 py-2 rounded-full flex items-center ${
                    !walletAddress
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-amber-600 hover:bg-amber-700 text-white transition-colors shadow-sm"
                  }`}
                >
                  <Download className="inline-block mr-2 w-5 h-5" />
                  Redeem IUs
                </button>
                <button
                  onClick={navigateToTaskBoard}
                  className="px-4 py-2 rounded-full flex items-center bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-sm"
                >
                  <ListTodo className="inline-block mr-2 w-5 h-5" />
                  Task Board
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Project Actions */}
        <div className="mb-8 flex flex-wrap gap-3 justify-end">
          {checkIfProjectCreator() && (
            <>
              <button
                onClick={handleEditProject}
                className="flex items-center gap-1 px-4 py-2 bg-white text-indigo-600 hover:bg-indigo-50 border border-indigo-300 rounded-lg shadow-sm"
              >
                <Edit2 className="h-4 w-4" />
                <span>Edit Project</span>
              </button>
              {/* <button
                onClick={handleCreateTask}
                className="flex items-center gap-1 px-4 py-2 bg-white text-green-600 hover:bg-green-50 border border-green-300 rounded-lg shadow-sm"
              >
                <ListPlus className="h-4 w-4" />
                <span>Create Task</span>
              </button> */}
            </>
          )}
        </div>

        {/* Creator notification - only shown if the user is identified as a project creator */}
        {isProjectCreator && (
          <div className="bg-green-50 p-5 mb-8 rounded-xl border border-green-200 flex items-center justify-between shadow-sm">
            <div className="flex items-center">
              <div className="bg-green-100 p-2.5 rounded-lg mr-4">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-green-800 font-medium flex items-center text-lg">
                  Project Creator Access
                  <span 
                    className="ml-2 cursor-help text-xs bg-green-200 text-green-700 rounded-full px-2 py-0.5"
                    title="You are identified as a project creator based on the blockchain data. This gives you special editing privileges."
                  >
                    ℹ️ What's this?
                  </span>
                </h3>
                <p className="text-green-700 text-sm mt-1">You have creator rights for this project and can edit project details and manage tasks.</p>
              </div>
            </div>
          </div>
        )}

        {/* Project Metrics with refresh button */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-700 flex items-center">
            <div className="bg-indigo-100 p-1.5 rounded-lg mr-2">
              <Zap className="h-5 w-5 text-indigo-600" />
            </div>
            Project Metrics
          </h2>
          {isReady && (
            <button 
              onClick={refreshAllProjectData} 
              disabled={loadingContractData || loadingIUAllocation || loadingInnovators || loadingEmissions}
              className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm px-4 py-2 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm"
              title="Refresh all blockchain data"
            >
              {(loadingContractData || loadingIUAllocation || loadingInnovators || loadingEmissions) ? (
                <>
                  <LucideLoaderCircle className="animate-spin h-4 w-4" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Refresh All Data</span>
                </>
              )}
            </button>
          )}
        </div>
        
        {/* Project Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Treasury Balance */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl shadow-md border border-blue-200 hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-700 text-sm font-medium flex items-center">
                Treasury Balance
                <span className="ml-1 text-xs text-blue-500 cursor-help" title="Balance of CEL tokens in the project treasury. Live data from blockchain.">
                  (On-chain)
                </span>
              </p>
              <div className="rounded-full bg-blue-100 p-2">
                <Wallet className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            {loadingContractData && !projectTreasury.raw ? (
              <div className="flex items-center h-8 mt-2">
                <LucideLoaderCircle className="animate-spin h-5 w-5 text-blue-500" />
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-2xl font-bold text-blue-800">
                  {projectTreasury.formatted ? parseFloat(projectTreasury.formatted).toFixed(2) : '0.00'}
                  <span className="text-sm font-medium text-blue-600"> CEL</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">Available for IU redemptions</p>
              </div>
            )}
          </div>
          
          {/* Staking Score */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl shadow-md border border-green-200 hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-700 text-sm font-medium flex items-center">
                Staking Score
                <span className="ml-1 text-xs text-green-500 cursor-help" title="Total staking score of all stakers, calculated based on amount and lock duration. Higher score means more staking impact. Live data from blockchain.">
                  (On-chain)
                </span>
              </p>
              <div className="rounded-full bg-green-100 p-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
            </div>
            {loadingContractData && !projectStakingScore.raw ? (
              <div className="flex items-center h-8 mt-2">
                <LucideLoaderCircle className="animate-spin h-5 w-5 text-green-500" />
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-2xl font-bold text-green-800">
                  {projectStakingScore.formatted ? parseFloat(projectStakingScore.formatted).toFixed(2) : '0.00'} 
                  <span className="text-sm font-medium text-green-600"> points</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">Time-weighted staking impact</p>
              </div>
            )}
          </div>
          
          {/* Total Staked Amount */}
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl shadow-md border border-purple-200 hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-700 text-sm font-medium flex items-center">
                Total Staked
                <span className="ml-1 text-xs text-purple-500 cursor-help" title="Total amount of CEL tokens staked on this project. Live data from blockchain.">
                  (On-chain)
                </span>
              </p>
              <div className="rounded-full bg-purple-100 p-2">
                <Lock className="h-4 w-4 text-purple-600" />
              </div>
            </div>
            {loadingContractData && !projectTotalStaked.raw ? (
              <div className="flex items-center h-8 mt-2">
                <LucideLoaderCircle className="animate-spin h-5 w-5 text-purple-500" />
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-2xl font-bold text-purple-800">
                  {projectTotalStaked.formatted ? parseFloat(projectTotalStaked.formatted).toFixed(2) : '0.00'}
                  <span className="text-sm font-medium text-purple-600"> CEL</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">Raw amount staked by all users</p>
              </div>
            )}
          </div>
          
          {/* IU Circulating Supply */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-5 rounded-xl shadow-md border border-amber-200 hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-700 text-sm font-medium flex items-center">
                IU Circulating Supply
                <span className="ml-1 text-xs text-amber-500 cursor-help" title="Total number of Innovation Units (IUs) currently in circulation for this project. Live data from blockchain.">
                  (On-chain)
                </span>
              </p>
              <div className="rounded-full bg-amber-100 p-2">
                <Coins className="h-4 w-4 text-amber-600" />
              </div>
            </div>
            {loadingContractData ? (
              <div className="flex items-center h-8 mt-2">
                <LucideLoaderCircle className="animate-spin h-5 w-5 text-amber-500" />
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-2xl font-bold text-amber-800">
                  {circulatingSupply.formatted !== undefined ? circulatingSupply.formatted : '0'}
                  <span className="text-sm font-medium text-amber-600"> IUs</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">Total tokens in circulation</p>
              </div>
            )}
          </div>
        </div>

        {/* Task Summary Section - moved up */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center">
                <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                  <ListTodo className="h-5 w-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-semibold">Task Board</h2>
              </div>
            </div>

            {loadingTasks ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="py-8 text-center">
                <div className="bg-gray-50 max-w-md mx-auto p-6 rounded-xl border border-gray-200">
                  <div className="bg-indigo-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <ListTodo className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Tasks Yet</h3>
                  <p className="text-gray-600 mb-4">This project doesn't have any tasks yet.</p>
                  {isProjectCreator && (
                    <button
                      onClick={handleCreateTask}
                      className="mt-2 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <ListPlus className="mr-2 h-4 w-4" />
                      Create First Task
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
               
                {/* Mini Kanban Board Preview */}
                <div className="mb-6">
                 
                  
                  <div className="overflow-x-auto pb-2 hide-scrollbar">
                    <div className="flex space-x-4 min-w-full">
                      {/* Only show the most important columns: Open, In Progress, Submitted */}
                      {['open', 'in_progress', 'submitted'].map(columnId => {
                        const columnTasks = tasks.filter(t => t.status === columnId);
                        return (
                          <div key={columnId} className="min-w-[250px] w-1/3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                            {/* Column Header */}
                            <div className={`p-3 ${
                              columnId === 'open' ? 'bg-sky-100' : 
                              columnId === 'in_progress' ? 'bg-violet-100' : 
                              'bg-amber-100'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className={`h-3 w-3 rounded-full ${
                                    columnId === 'open' ? 'bg-sky-500' : 
                                    columnId === 'in_progress' ? 'bg-violet-500' : 
                                    'bg-amber-500'
                                  }`}></span>
                                  <span className="font-medium text-gray-800 capitalize">{columnId.replace('_', ' ')}</span>
                                </div>
                                <span className="bg-white text-gray-700 text-xs font-medium px-2 py-1 rounded-full">{columnTasks.length}</span>
                              </div>
                            </div>
                            
                            {/* Column Tasks - limit to 3 for preview */}
                            <div className="p-2">
                              {columnTasks.length === 0 ? (
                                <div className="py-4 text-center text-gray-500 text-sm italic">
                                  No tasks
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {columnTasks.slice(0, 3).map(task => (
                                    <div 
                                      key={task._id} 
                                      className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow transition-shadow cursor-pointer"
                                      onClick={() => navigateToTaskBoard()}
                                    >
                                      <h4 className="font-medium text-gray-800 text-sm mb-1 line-clamp-1">{task.title}</h4>
                                      <div className="flex items-center justify-between">
                                        <div className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                          {task.taskPoints} IU
                                        </div>
                                        {task.assigneeWalletAddress && (
                                          <div className="flex items-center text-xs text-gray-500">
                                            <User className="h-3 w-3 mr-1" />
                                            <span>{task.assigneeWalletAddress.slice(0, 6)}...</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  
                                  {/* Show "more tasks" indicator if there are more than 3 */}
                                  {columnTasks.length > 3 && (
                                    <div 
                                      className="text-center py-1 text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer"
                                      onClick={() => navigateToTaskBoard()}
                                    >
                                      + {columnTasks.length - 3} more tasks
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Show message about additional columns */}
                  <div className="mt-2 text-xs text-gray-500 text-center">
                    <p>Preview shows 3 key columns. View the full task board to see all columns and tasks.</p>
                  </div>
                </div>
                
                
                
                {/* Call to action for Task Board */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-5 border border-indigo-100 flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-indigo-800 mb-1">View Full Task Board</h3>
                    <p className="text-indigo-600">Access our interactive Kanban board to view, manage and interact with all project tasks.</p>
                  </div>
                  <button
                    onClick={navigateToTaskBoard}
                    className="flex items-center px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                  >
                    <span>Open Task Board</span>
                    <MoveRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Project Innovators Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center">
              <div className="bg-purple-100 p-1.5 rounded-lg mr-2">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              Project Innovators
            </h3>
            {isReady && (
              <button 
                onClick={refreshAllProjectData} 
                disabled={loadingContractData || loadingIUAllocation || loadingInnovators || loadingEmissions}
                className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm px-4 py-2 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm"
                title="Refresh innovators data from blockchain"
              >
                {(loadingContractData || loadingIUAllocation || loadingInnovators || loadingEmissions) ? (
                  <>
                    <LucideLoaderCircle className="animate-spin h-4 w-4" />
                    <span>Refreshing...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh Innovators</span>
                  </>
                )}
              </button>
            )}
          </div>
          
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <InnovatorsTable
              creators={contractCreators.length > 0 ? contractCreators : (project.creators || [])}
              contributors={contractContributors.length > 0 ? contractContributors : (project.contributors || [])}
              investors={contractInvestors.length > 0 ? contractInvestors : (project.investors || [])}
              stakers={contractStakers.length > 0 ? contractStakers : (project.stakers || [])}
            />
          </div>
        </div>

        {/* Project Content */}
        <div className="grid grid-cols-1 gap-8">
          {/* Main Content - full width */}
          <div className="space-y-8">
            {/* Description */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <div className="bg-blue-100 p-1.5 rounded-lg mr-2">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                </div>
                About the Project
              </h2>
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-700 mb-2">
                  Short Description
                </h3>
                <p className="text-gray-600 border border-gray-200 rounded-xl p-4 bg-gray-50 mt-1">
                  {project.shortDescription || project.description}
                </p>
              </div>
              <div>
                <h3 className="text-md font-medium text-gray-700 mb-2">
                  Detailed Description
                </h3>
                <p className="text-gray-600 bg-gray-50 border border-gray-200 rounded-xl p-4 mt-1">
                  {project.detailedDescription || project.problemAddressed}
                </p>
              </div>
            </div>

            {/* Cards Container */}
            <div className="flex flex-col md:flex-row gap-6 mb-8">
              {(project.intendedAudience?.length > 0 ||
                project.targetedPlatforms?.length > 0) && (
                <div className="flex-1 bg-gray-100 rounded-xl shadow-sm p-6 border border-gray-400">
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <div className="bg-green-100 p-1.5 rounded-lg mr-2">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    Target Information
                  </h2>

                  {project.intendedAudience &&
                    project.intendedAudience.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-md font-medium text-gray-700 mb-3">
                          Intended Audience
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {project.intendedAudience.map((audience, index) => (
                            <span
                              key={index}
                              className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium"
                            >
                              {audience}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {project.targetedPlatforms &&
                    project.targetedPlatforms.length > 0 && (
                      <div>
                        <h3 className="text-md font-medium text-gray-700 mb-3">
                          Targeted Platforms
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {project.targetedPlatforms.map((platform, index) => (
                            <span
                              key={index}
                              className="bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-medium"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* External Links Card */}
              {(project.relevantUrls?.length > 0 || project.urls) && (
                <div className="flex-1 bg-gray-100 rounded-xl shadow-sm p-6 border border-gray-400">
                  <h2 className="text-xl font-semibold mb-6 flex items-center">
                    <div className="bg-blue-100 p-1.5 rounded-lg mr-2">
                      <ExternalLink className="h-5 w-5 text-blue-600" />
                    </div>
                    External Links
                  </h2>
                  {project.relevantUrls && project.relevantUrls.length > 0 ? (
                    <div className="space-y-3">
                      {project.relevantUrls.map(
                        (url, index) =>
                          url && (
                            <a
                              key={index}
                              href={
                                url.startsWith("http") ? url : `https://${url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-indigo-600 hover:text-indigo-700 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4 mr-3 flex-shrink-0" />
                              <span className="truncate">{url}</span>
                            </a>
                          )
                      )}
                    </div>
                  ) : (
                    <a
                      href={project.urls}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-indigo-600 hover:text-indigo-700 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 mr-3" />
                      <span className="truncate">{project.urls}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Tokenomics */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold mb-4 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="bg-green-100 p-1.5 rounded-lg mr-2">
                    <Coins className="h-5 w-5 text-green-600" />
                  </div>
                  <span>Tokenomics & Investment</span>
                </div>
                {isReady && (
                  <button 
                    onClick={refreshAllProjectData} 
                    disabled={loadingContractData || loadingIUAllocation || loadingInnovators || loadingEmissions}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded-md border border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm"
                    title="Refresh allocation data from blockchain"
                  >
                    {(loadingIUAllocation || loadingInnovators || loadingEmissions) ? (
                      <>
                        <LucideLoaderCircle className="animate-spin h-4 w-4" />
                        <span>Refreshing...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh Allocations</span>
                      </>
                    )}
                  </button>
                )}
              </h2>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl mb-5 border-l-4 border-green-500">
                <p className="text-sm text-gray-700">
                  Innovation Units (IUs) represent ownership in this project and can be purchased by investors or earned by contributors. 
                  The price is set by the project's initial valuation. Holders of IUs are eligible for rewards from the project's success.
                </p>
              </div>
              
              <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                  <PieChart className="h-4 w-4 mr-2 text-indigo-600" />
                  Total Supply & Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                    <p className="text-md font-medium text-gray-600 flex items-center">
                      Innovation Units Total Supply
                      <span className="ml-1 text-xs text-indigo-500 cursor-help" title="Total supply of Innovation Units for this project. Live data from blockchain.">
                        (On-chain)
                      </span>
                    </p>
                    {loadingContractData && !projectSupply.raw ? (
                      <div className="flex items-center h-8 mt-2">
                        <LucideLoaderCircle className="animate-spin h-5 w-5 text-indigo-500" />
                      </div>
                    ) : (
                      <div>
                        <p className="text-2xl font-semibold mt-2 text-indigo-800">
                          {projectSupply.formatted} 
                          <span className="text-sm text-indigo-600"> IUs</span>
                        </p>
                       
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow">
                    <p className="text-md font-medium text-gray-600 flex items-center">
                      Price per IU
                      <span className="ml-1 text-xs text-indigo-500 cursor-help" title="Initial price per Innovation Unit. Live data from blockchain.">
                        (On-chain)
                      </span>
                    </p>
                    {loadingContractData && !projectPrice.raw ? (
                      <div className="flex items-center h-8 mt-2">
                        <LucideLoaderCircle className="animate-spin h-5 w-5 text-indigo-500" />
                      </div>
                    ) : (
                      <div>
                        <p className="text-2xl font-semibold mt-2 text-green-800">
                          {projectPrice.formatted ? parseFloat(projectPrice.formatted).toFixed(2) : '0.00'} 
                          <span className="text-sm text-green-600"> CEL</span>
                        </p>
                        {project.initialPrice && projectPrice.formatted !== project.initialPrice.toString() && (
                          <p className="text-xs text-amber-600 mt-1">
                            Database price: {project.initialPrice} CEL
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                  <Users className="h-4 w-4 mr-2 text-purple-600" />
                  Allocation Availability
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow relative overflow-hidden">
                    <div className="relative z-10">
                      <p className="text-md font-medium text-gray-600 flex items-center">
                        Remaining IUs for Investors
                        <span className="ml-1 text-xs text-green-500 cursor-help" title="Real-time data from blockchain contract">
                          (On-chain)
                        </span>
                      </p>
                      {loadingIUAllocation && !remainingInvestorIUs.raw ? (
                        <div className="flex items-center h-8 mt-2">
                          <LucideLoaderCircle className="animate-spin h-5 w-5 text-green-500" />
                        </div>
                      ) : (
                        <div>
                          <p className="text-2xl font-semibold mt-2 text-green-800">
                            {remainingInvestorIUs.formatted} 
                            <span className="text-sm text-green-600"> IUs</span>
                          </p>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs mr-2">
                              {project.ownershipDistribution?.investors || 0}%
                            </span>
                            <span>
                              {parseInt(remainingInvestorIUs.formatted) === 0 ? 
                                "All investor tokens have been purchased" : 
                                "Available for purchase"
                              }
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Progress overlay */}
                    {projectSupply.raw && remainingInvestorIUs.raw && (
                      <div className="absolute bottom-0 left-0 h-1 bg-green-200 w-full">
                        <div 
                          className="h-full bg-green-500" 
                          style={{ 
                            width: `${100 - (parseInt(remainingInvestorIUs.formatted) / (projectSupply.formatted * (project.ownershipDistribution?.investors || 0) / 100) * 100)}%` 
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 hover:shadow-sm transition-shadow relative overflow-hidden">
                    <div className="relative z-10">
                      <p className="text-md font-medium text-gray-600 flex items-center">
                        Remaining IUs for Contributors
                        <span className="ml-1 text-xs text-blue-500 cursor-help" title="Real-time data from blockchain contract">
                          (On-chain)
                        </span>
                      </p>
                      {loadingIUAllocation && !remainingContributorIUs.raw ? (
                        <div className="flex items-center h-8 mt-2">
                          <LucideLoaderCircle className="animate-spin h-5 w-5 text-blue-500" />
                        </div>
                      ) : (
                        <div>
                          <p className="text-2xl font-semibold mt-2 text-blue-800">
                            {remainingContributorIUs.formatted}
                            <span className="text-sm text-blue-600"> IUs</span>
                          </p>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs mr-2">
                              {project.ownershipDistribution?.contributors || 0}%
                            </span>
                            <span>
                              {parseInt(remainingContributorIUs.formatted) === 0 ? 
                                "All contributor tokens have been claimed" : 
                                "Available for task completion"
                              }
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Progress overlay */}
                    {projectSupply.raw && remainingContributorIUs.raw && (
                      <div className="absolute bottom-0 left-0 h-1 bg-blue-200 w-full">
                        <div 
                          className="h-full bg-blue-500" 
                          style={{ 
                            width: `${100 - (parseInt(remainingContributorIUs.formatted) / (projectSupply.formatted * (project.ownershipDistribution?.contributors || 0) / 100) * 100)}%` 
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={handleInvest}
                  data-action="invest"
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    !walletAddress
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm"
                  }`}
                >
                  <DollarSign className="inline-block mr-2 w-4 h-4" />
                  Invest in Project
                </button>
                <button
                  onClick={handleRedeem}
                  data-action="redeem"
                  className={`px-4 py-2 rounded-lg flex items-center ${
                    !walletAddress
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-amber-600 hover:bg-amber-700 text-white transition-colors shadow-sm"
                  }`}
                >
                  <Download className="inline-block mr-2 w-4 h-4" />
                  Redeem IUs
                </button>
              </div> */}
            </div>

            {/* Emissions History */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <div className="bg-indigo-100 p-1.5 rounded-lg mr-2">
                    <Zap className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Epoch Emissions History</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      CEL emissions earned by project participants over time
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {process.env.NODE_ENV === 'development' && (
                    <button 
                      onClick={() => {
                        console.log("=== EMISSIONS DATA STRUCTURE ===");
                        console.log("Full emissions data:", emissionsData);
                        if (emissionsData && emissionsData.length > 0) {
                          const totalSum = emissionsData.reduce((sum, emission) => {
                            const parsedAmount = parseFloat(emission.amount || 0);
                            return isNaN(parsedAmount) ? sum : sum + parsedAmount;
                          }, 0);
                        }
                      }}
                      className="mr-2 px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs"
                    >
                      Debug
                    </button>
                  )}
                  {isReady && services?.emissionController && (
                    <button 
                      onClick={fetchHistoricalEmissions} 
                      disabled={loadingEmissions}
                      className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm px-4 py-2 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors shadow-sm"
                      title="Refresh emissions data from blockchain"
                    >
                      {loadingEmissions ? (
                        <>
                          <LucideLoaderCircle className="animate-spin h-4 h-4" />
                          <span>Refreshing...</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 h-4" />
                          <span>Refresh Emissions</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-xl mb-6 border-l-4 border-indigo-500">
                <p className="text-sm text-gray-700">
                  CEL token emissions are distributed every epoch (typically 7-30 days) to project participants. 
                  Projects earn emissions based on their staking score and activities. These rewards are split between 
                  Innovation Unit holders and CEL stakers supporting the project.
                </p>
              </div>

              {loadingEmissions && emissionsData.length === 0 ? (
                <div className="flex justify-center items-center py-16">
                  <div className="flex flex-col items-center">
                    <LucideLoaderCircle className="animate-spin h-12 w-12 text-indigo-600 mb-4" />
                    <span className="text-indigo-600 font-medium">Loading emissions data...</span>
                  </div>
                </div>
              ) : emissionsData.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-b from-indigo-50 to-white rounded-lg border border-indigo-100">
                  <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Droplets className="w-8 h-8 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Emissions Data Available</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    This project hasn't received any emissions yet. Emissions data will appear once the project participates in at least one epoch.
                  </p>
                </div>
              ) : (
                <>
                  {/* Emissions Metrics Cards */}
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white p-5 rounded-xl shadow-md">
                      <div className="flex items-center">
                        <Clock className="h-6 w-6 text-indigo-200 mr-3" />
                        <div>
                          <p className="text-indigo-200 text-sm font-medium uppercase tracking-wider">Current Epoch</p>
                          <p className="text-2xl font-bold mt-1">
                            {emissionsData && emissionsData.length > 0 
                              ? `#${emissionsData[0].epochNumber || '--'}` 
                              : 'Not started'}
                          </p>
                        </div>
                      </div>
                      {emissionsData && emissionsData.length > 0 && emissionsData[0].endTime && (
                        <div className="mt-3 text-xs text-indigo-200">
                          <div className="bg-indigo-400/20 rounded-lg px-3 py-2">
                            Ends on {new Date(emissionsData[0].endTime * 1000).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-5 rounded-xl shadow-md">
                      <div className="flex items-center">
                        <DollarSign className="h-6 w-6 text-emerald-200 mr-3" />
                        <div>
                          <p className="text-emerald-200 text-sm font-medium uppercase tracking-wider">Last Emission</p>
                          <p className="text-2xl font-bold mt-1">
                            {emissionsData && emissionsData.length > 0 
                              ? (() => {
                                  // Handle different possible data structures
                                  const emission = emissionsData[0];
                                  
                                  // Try different possible property paths
                                  let amount = null;
                                  if (typeof emission.amount === 'number') {
                                    amount = emission.amount;
                                  } else if (typeof emission.amount === 'string') {
                                    amount = parseFloat(emission.amount);
                                  } else if (emission.amount && typeof emission.amount.formatted === 'string') {
                                    amount = parseFloat(emission.amount.formatted);
                                  } else if (emission.amount && typeof emission.amount.raw === 'object') {
                                    // Handle BigNumber objects
                                    try {
                                      amount = parseFloat(ethers.utils.formatEther(emission.amount.raw));
                                    } catch (e) {
                                      console.error("Error formatting BigNumber:", e);
                                    }
                                  } else if (emission.formatted) {
                                    amount = parseFloat(emission.formatted);
                                  } else if (emission.totalEmissions && emission.totalEmissions.formatted) {
                                    amount = parseFloat(emission.totalEmissions.formatted);
                                  }
                                  
                                  // Return formatted amount or default
                                  return !isNaN(amount) && amount > 0 
                                    ? `${amount.toFixed(2)} CEL` 
                                    : '0.00 CEL';
                                })()
                              : '0.00 CEL'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 text-xs">
                        <div className="bg-emerald-400/20 rounded-lg px-3 py-2 text-emerald-100">
                          {emissionsData && emissionsData.length > 0 && (
                            (() => {
                              // Try to extract and parse amount
                              const emission = emissionsData[0];
                              let amount = 0;
                              
                              if (typeof emission.amount === 'number') {
                                amount = emission.amount;
                              } else if (typeof emission.amount === 'string') {
                                amount = parseFloat(emission.amount);
                              } else if (emission.amount && typeof emission.amount.formatted === 'string') {
                                amount = parseFloat(emission.amount.formatted);
                              } else if (emission.totalEmissions && emission.totalEmissions.formatted) {
                                amount = parseFloat(emission.totalEmissions.formatted);
                              }
                              
                              return !isNaN(amount) && amount > 0 
                                ? 'Earned in the last completed epoch' 
                                : 'No emissions in last epoch';
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white p-5 rounded-xl shadow-md">
                      <div className="flex items-center">
                        <PieChart className="h-6 w-6 text-amber-200 mr-3" />
                        <div>
                          <p className="text-amber-200 text-sm font-medium uppercase tracking-wider">Total Emissions</p>
                          <p className="text-2xl font-bold mt-1">
                            {emissionsData && emissionsData.length > 0
                              ? (() => {
                                  // Calculate total emissions with flexible property path handling
                                  const total = emissionsData.reduce((sum, emission) => {
                                    let amount = 0;
                                    
                                    // Try different property paths
                                    if (typeof emission.amount === 'number') {
                                      amount = emission.amount;
                                    } else if (typeof emission.amount === 'string') {
                                      amount = parseFloat(emission.amount);
                                    } else if (emission.amount && typeof emission.amount.formatted === 'string') {
                                      amount = parseFloat(emission.amount.formatted);
                                    } else if (emission.totalEmissions && emission.totalEmissions.formatted) {
                                      amount = parseFloat(emission.totalEmissions.formatted);
                                    }
                                    
                                    return !isNaN(amount) ? sum + amount : sum;
                                  }, 0);
                                  
                                  return `${total.toFixed(2)} CEL`;
                                })()
                              : '0.00 CEL'}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 text-xs">
                        <div className="bg-amber-400/20 rounded-lg px-3 py-2 text-amber-100">
                          {emissionsData.length > 1 
                            ? `Across ${emissionsData.length} epochs` 
                            : emissionsData.length === 1 
                              ? 'From 1 epoch' 
                              : 'No epochs recorded'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Emissions History Table */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-semibold text-gray-800">Emission History Log</h3>
                      <button
                        onClick={() => setExpandedTable(!expandedTable)}
                        className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        {expandedTable ? 'Show Less' : 'Show All'}
                        <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-300 ${expandedTable ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                    
                    <div className="overflow-hidden rounded-xl border border-indigo-100 bg-white shadow-sm">
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-indigo-100">
                        <h3 className="text-md font-semibold text-indigo-800">Emission Details</h3>
                      </div>
                      
                      <EmissionsHistoryTable 
                        emissionsData={expandedTable ? emissionsData : emissionsData.slice(0, 3)}
                        loading={loadingEmissions}
                      />
                      
                      {/* Show pagination indicator if table is collapsed and there's more data */}
                      {!expandedTable && emissionsData.length > 3 && (
                        <div className="bg-indigo-50/50 py-3 text-center">
                          <button
                            onClick={() => setExpandedTable(true)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center justify-center mx-auto"
                          >
                            Show {emissionsData.length - 3} more epochs
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Investment Modal */}
      {showInvestForm && (
        <InvestIUModal
          project={project}
          isOpen={showInvestForm}
          onClose={() => handleCloseModal('invest')}
          getExplorerUrl={getExplorerUrl}
          onSuccess={handleInvestSuccess}
          userCelBalance={userCelBalance}
          remainingInvestorIUs={remainingInvestorIUs}
        />
      )}

      {/* Staking Modal */}
      {showStakeForm && (
        <StakeProjectModal
          project={project}
          isOpen={showStakeForm}
          onClose={() => handleCloseModal('stake')}
          getExplorerUrl={getExplorerUrl}
          onSuccess={handleStakeSuccess}
        />
      )}

      {/* Redeem Modal */}
      {showRedeemForm && (
        <RedeemIUModal
          project={project}
          isOpen={showRedeemForm}
          onClose={() => handleCloseModal('redeem')}
          getExplorerUrl={getExplorerUrl}
          onSuccess={handleRedeemSuccess}
        />
      )}

      {/* Edit Project Form */}
      {showEditForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <EditProjectForm 
              project={project} 
              onClose={() => handleCloseModal('edit-project', true)} 
            />
          </div>
        </div>
      )}

      {showTaskForm && (
        <TaskFormModal
          open={showTaskForm}
          onClose={() => setShowTaskForm(false)}
          initialTask={null}
              project={project} 
          onSave={() => {fetchTasks(project._id)}}
          innovators={innovators}
          isConnected={isConnected}
            />
      )}
    </div>
  );
}

export default ProjectDetails;